<div class="aca-admin aca-pdpa">
  <aca-page-header [bare]="embedded" icon="policy" [title]="'DMS.PDPA.TITLE' | translate" [subtitle]="'DMS.PDPA.SUBTITLE' | translate">
    <button type="button" class="aca-pdpa__primary" (click)="start()" [disabled]="starting() || running() || loading()">
      <mat-icon>manage_search</mat-icon>{{ (report() ? 'DMS.PDPA.RESCAN' : 'DMS.PDPA.SCAN') | translate }}
    </button>
  </aca-page-header>

  <mat-progress-bar mode="indeterminate" *ngIf="loading()" />

  <aca-page-state *ngIf="failed()" state="error" [title]="failed()" [actionLabel]="'DMS.PDPA.RETRY' | translate" (action)="load()" />

  <!-- ── never scanned ──────────────────────────────────────────────────── -->
  <aca-page-state
    *ngIf="!loading() && !failed() && !report()"
    state="empty"
    icon="policy"
    [title]="'DMS.PDPA.NEVER_TITLE' | translate"
    [message]="'DMS.PDPA.NEVER_HINT' | translate"
    [actionLabel]="'DMS.PDPA.SCAN' | translate"
    (action)="start()"
  />

  <ng-container *ngIf="!failed() && report() as r">
    <!-- ── the scan ─────────────────────────────────────────────────────── -->
    <section class="aca-pdpa__run" [class.aca-pdpa__run--bad]="r.status === 'failed'">
      <div class="aca-pdpa__runline">
        <span class="aca-pdpa__status" [ngClass]="'aca-pdpa__status--' + r.status">{{ statusKey(r) | translate }}</span>
        <span class="aca-pdpa__runmeta">
          {{ 'DMS.PDPA.STARTED' | translate: { by: r.startedBy } }} {{ r.startedAt | acaDate: 'd MMM y HH:mm' }}
          <ng-container *ngIf="r.finishedAt"> · {{ 'DMS.PDPA.FINISHED' | translate }} {{ r.finishedAt | acaDate: 'd MMM y HH:mm' }}</ng-container>
        </span>
        <button type="button" class="aca-pdpa__ghost" *ngIf="running()" (click)="cancel()">
          <mat-icon>stop_circle</mat-icon>{{ 'DMS.PDPA.CANCEL' | translate }}
        </button>
      </div>
      <ng-container *ngIf="running()">
        <mat-progress-bar mode="determinate" [value]="progress()" />
        <p class="aca-pdpa__runmeta">{{ 'DMS.PDPA.PROGRESS' | translate: { scanned: r.scanned, total: r.total } }}</p>
      </ng-container>
      <p class="aca-pdpa__error" *ngIf="r.error">{{ r.error }}</p>
    </section>

    <!-- ── at a glance ──────────────────────────────────────────────────── -->
    <div class="aca-pdpa__tiles">
      <div class="aca-pdpa__tile">
        <span class="aca-pdpa__tilevalue">{{ r.scanned | number }}</span>
        <span class="aca-pdpa__tilelabel">{{ 'DMS.PDPA.TILE_SCANNED' | translate }}</span>
      </div>
      <button
        type="button"
        class="aca-pdpa__tile aca-pdpa__tile--warn"
        [class.aca-pdpa__tile--on]="kindFilter() === 'all' && !openOnly()"
        (click)="kindFilter.set('all'); openOnly.set(false)"
      >
        <span class="aca-pdpa__tilevalue">{{ r.findings.length | number }}</span>
        <span class="aca-pdpa__tilelabel">{{ 'DMS.PDPA.TILE_FOUND' | translate }}</span>
      </button>
      <button type="button" class="aca-pdpa__tile aca-pdpa__tile--bad" [class.aca-pdpa__tile--on]="openOnly()" (click)="openOnly.set(!openOnly())">
        <span class="aca-pdpa__tilevalue">{{ openCount() | number }}</span>
        <span class="aca-pdpa__tilelabel">{{ 'DMS.PDPA.TILE_OPEN' | translate }}</span>
      </button>
      <button type="button" class="aca-pdpa__tile" (click)="showUnread.set(!showUnread())">
        <span class="aca-pdpa__tilevalue">{{ unreadTotal() | number }}</span>
        <span class="aca-pdpa__tilelabel">{{ 'DMS.PDPA.TILE_UNREAD' | translate }}</span>
      </button>
    </div>

    <div class="aca-pdpa__kinds">
      <button
        type="button"
        class="aca-pdpa__kind"
        *ngFor="let kind of kinds"
        [class.aca-pdpa__kind--on]="kindFilter() === kind"
        [disabled]="!r.documents[kind]"
        (click)="kindFilter.set(kindFilter() === kind ? 'all' : kind)"
      >
        {{ kindKey(kind) | translate }} <strong>{{ r.documents[kind] ?? 0 | number }}</strong>
      </button>
    </div>

    <!-- ── documents not read ───────────────────────────────────────────── -->
    <section class="aca-pdpa__card aca-pdpa__unread" *ngIf="showUnread() && unreadTotal()">
      <h3 class="aca-pdpa__cardtitle">{{ 'DMS.PDPA.UNREAD_TITLE' | translate }}</h3>
      <ul class="aca-pdpa__reasons">
        <ng-container *ngFor="let reason of reasons">
          <li *ngIf="r.unreadCounts[reason] as count">
            <strong>{{ count | number }}</strong> {{ reasonKey(reason) | translate }}
          </li>
        </ng-container>
      </ul>
      <table class="aca-pdpa__table">
        <tbody>
          <tr class="aca-pdpa__row" *ngFor="let item of r.unread; trackBy: trackByNode">
            <td class="aca-pdpa__td">
              <button type="button" class="aca-pdpa__name" (click)="openDocument(item.nodeId)">{{ item.name }}</button>
              <span class="aca-pdpa__sub">{{ item.path }}</span>
            </td>
            <td class="aca-pdpa__td aca-pdpa__td--muted">{{ reasonKey(item.reason) | translate }}</td>
          </tr>
        </tbody>
      </table>
      <p class="aca-pdpa__sub" *ngIf="unreadTotal() > r.unread.length">{{ 'DMS.PDPA.UNREAD_MORE' | translate: { shown: r.unread.length } }}</p>
    </section>

    <!-- ── findings ─────────────────────────────────────────────────────── -->
    <aca-page-state
      *ngIf="!visible().length && !running()"
      state="empty"
      icon="verified_user"
      [title]="(r.findings.length ? 'DMS.PDPA.EMPTY_FILTER' : 'DMS.PDPA.EMPTY') | translate"
      [message]="'DMS.PDPA.EMPTY_HINT' | translate"
    />

    <section class="aca-pdpa__card" *ngIf="visible().length">
      <div class="aca-pdpa__tablewrap">
        <table class="aca-pdpa__table">
          <thead>
            <tr>
              <th class="aca-pdpa__th">{{ 'DMS.PDPA.COL_DOC' | translate }}</th>
              <th class="aca-pdpa__th">{{ 'DMS.PDPA.COL_LEVEL' | translate }}</th>
              <th class="aca-pdpa__th">{{ 'DMS.PDPA.COL_FOUND' | translate }}</th>
              <th class="aca-pdpa__th aca-pdpa__th--actions"></th>
            </tr>
          </thead>
          <tbody>
            <tr class="aca-pdpa__row" *ngFor="let f of visible(); trackBy: trackByNode">
              <td class="aca-pdpa__td">
                <button type="button" class="aca-pdpa__name" (click)="openDocument(f.nodeId)">{{ f.name }}</button>
                <span class="aca-pdpa__sub">{{ f.path }}</span>
              </td>
              <td class="aca-pdpa__td">
                <span class="aca-pdpa__level" [class.aca-pdpa__level--open]="isOpen(f)">{{ levelKey(f) | translate }}</span>
              </td>
              <td class="aca-pdpa__td">
                <span class="aca-pdpa__chip" *ngFor="let kind of kindsOf(f)" [matTooltip]="samplesOf(f, kind)">
                  {{ kindKey(kind) | translate }} × {{ f.counts[kind] }}
                </span>
                <span class="aca-pdpa__sub" *ngIf="f.source === 'ocr'">{{ 'DMS.PDPA.FROM_OCR' | translate }}</span>
              </td>
              <td class="aca-pdpa__td aca-pdpa__td--actions">
                <button
                  mat-icon-button
                  class="aca-pdpa__iconaction"
                  (click)="openDocument(f.nodeId)"
                  [matTooltip]="'DMS.PDPA.OPEN' | translate"
                  [attr.aria-label]="'DMS.PDPA.OPEN' | translate"
                >
                  <mat-icon>open_in_new</mat-icon>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <p class="aca-pdpa__note"><mat-icon>info</mat-icon>{{ 'DMS.PDPA.NOTE' | translate }}</p>
  </ng-container>
</div>
